import { motion } from "framer-motion";
import { AudioWaveform, LockKeyhole, Mic2, Play, ScissorsLineDashed, Square, Trash2, TriangleAlert, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  cleanPcmRecording,
  cleanVoiceRecording,
  VOICE_TRIM_LEADING_PAD_MS,
  VOICE_TRIM_TRAILING_PAD_MS
} from "../audioTrim";
import {
  deleteVoiceLine,
  loadVoiceLineState,
  preferredRecordingMimeType,
  saveVoiceLine,
  VOICE_LINE_PROMPTS,
  type StoredVoiceLine,
  type VoiceLineGroup,
  type VoiceLineId,
  type VoiceStorageStatus
} from "../voiceLines";

const MAX_RECORDING_MS = 6_000;
const GROUPS: Array<{ id: VoiceLineGroup; label: string; color: string }> = [
  { id: "actions", label: "Your moves", color: "var(--yellow)" },
  { id: "blocks", label: "Your blocks", color: "var(--blue)" },
  { id: "responses", label: "Your callouts", color: "var(--coral)" }
];

/** Joins the tapped buffers into one track, or null if the tap caught nothing. */
function concatPcm(chunks: Float32Array[]): Float32Array | null {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  if (total === 0) return null;
  const samples = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    samples.set(chunk, offset);
    offset += chunk.length;
  }
  return samples;
}

function recordingFormatLabel(mimeType: string | undefined) {
  const normalized = mimeType?.toLowerCase() ?? "";
  if (normalized.includes("mp4")) return "MP4";
  if (normalized.includes("webm")) return "WebM";
  if (normalized.includes("ogg")) return "Ogg";
  return normalized.replace(/^audio\//, "").split(";")[0].toUpperCase() || "audio";
}

interface Props {
  onClose: () => void;
  onToast: (message: string) => void;
}

export function VoiceLinesSheet({ onClose, onToast }: Props) {
  const [clips, setClips] = useState<Map<VoiceLineId, StoredVoiceLine>>(new Map());
  const [requestingId, setRequestingId] = useState<VoiceLineId | null>(null);
  const [recordingId, setRecordingId] = useState<VoiceLineId | null>(null);
  const [processingId, setProcessingId] = useState<VoiceLineId | null>(null);
  const [playingId, setPlayingId] = useState<VoiceLineId | null>(null);
  const [storageStatus, setStorageStatus] = useState<VoiceStorageStatus | null>(null);
  const [error, setError] = useState("");
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  // Live PCM tapped off the microphone while MediaRecorder compresses in
  // parallel. This is what gets trimmed; the compressed take is only a fallback
  // for the case where the tap yields nothing.
  const pcmChunksRef = useRef<Float32Array[]>([]);
  const pcmSampleRateRef = useRef(0);
  const pcmTeardownRef = useRef<(() => void) | null>(null);
  const recordingTimeoutRef = useRef<number | null>(null);
  const discardRecordingRef = useRef(false);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    loadVoiceLineState()
      .then(({ records, storage }) => {
        setClips(new Map(records.map((record) => [record.id, record])));
        setStorageStatus(storage);
      })
      .catch(() => setError("This browser could not open local voice storage."));

    return () => {
      discardRecordingRef.current = true;
      if (recordingTimeoutRef.current) window.clearTimeout(recordingTimeoutRef.current);
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      // Closing the sheet mid-take must not leave an AudioContext holding the mic.
      pcmTeardownRef.current?.();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      previewAudioRef.current?.pause();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const stopRecording = () => {
    if (recordingTimeoutRef.current) window.clearTimeout(recordingTimeoutRef.current);
    recordingTimeoutRef.current = null;
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  /**
   * Copies raw microphone samples as they arrive, so trimming never depends on
   * a browser being able to decode its own compressed output.
   */
  const startPcmTap = (stream: MediaStream) => {
    pcmChunksRef.current = [];
    pcmSampleRateRef.current = 0;
    try {
      const AudioContextConstructor =
        window.AudioContext ??
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextConstructor) return;

      const context = new AudioContextConstructor();
      // Safari hands back a suspended context; Record is the user gesture that
      // makes resuming legal, and no samples arrive until it does.
      void context.resume();
      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      // A ScriptProcessor only runs while it reaches the destination, so it goes
      // through a silent gain node — otherwise the microphone plays aloud and
      // feeds straight back into itself.
      const silentSink = context.createGain();
      silentSink.gain.value = 0;

      pcmSampleRateRef.current = context.sampleRate;
      processor.onaudioprocess = (event) => {
        pcmChunksRef.current.push(new Float32Array(event.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(silentSink);
      silentSink.connect(context.destination);

      pcmTeardownRef.current = () => {
        processor.onaudioprocess = null;
        try {
          source.disconnect();
          processor.disconnect();
          silentSink.disconnect();
        } catch {
          // Already torn down by a stream that ended first.
        }
        void context.close();
        pcmTeardownRef.current = null;
      };
    } catch {
      // No tap: the compressed take and its decode path still apply.
      pcmChunksRef.current = [];
      pcmSampleRateRef.current = 0;
    }
  };

  const startRecording = async (id: VoiceLineId) => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Voice recording is not supported in this browser.");
      return;
    }

    try {
      setRequestingId(id);
      // This is the only microphone request in the prototype. It runs only
      // after the player taps Record for a specific cue.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      const mimeType = preferredRecordingMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      startPcmTap(stream);

      discardRecordingRef.current = false;
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      setRequestingId(null);
      setRecordingId(id);
      let recordingStartedAt = 0;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        pcmTeardownRef.current?.();
        const pcmChunks = pcmChunksRef.current;
        const pcmSampleRate = pcmSampleRateRef.current;
        pcmChunksRef.current = [];
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
        setRecordingId(null);
        if (discardRecordingRef.current) return;
        if (chunksRef.current.length === 0) {
          setError("No recording data arrived from the microphone. Please try again.");
          return;
        }

        setProcessingId(id);
        try {
          const sourceChunks = [...chunksRef.current];
          const sourceMimeType = recorder.mimeType || sourceChunks[0].type || "audio/mp4";
          const rawBlob = new Blob(sourceChunks, { type: sourceMimeType });
          const measuredDurationMs = Math.min(
            MAX_RECORDING_MS,
            Math.max(0, performance.now() - recordingStartedAt)
          );
          // Trim the samples we captured ourselves. Only if the tap produced
          // nothing do we fall back to decoding the compressed take, which is
          // the path that quietly gives up and keeps the whole recording.
          const pcmSamples = concatPcm(pcmChunks);
          const cleaned =
            pcmSamples && pcmSampleRate > 0
              ? cleanPcmRecording([pcmSamples], pcmSampleRate)
              : await cleanVoiceRecording(rawBlob, measuredDurationMs);
          const record: StoredVoiceLine = {
            id,
            blob: cleaned.blob,
            cleanupVersion: 1,
            cleanupMode: cleaned.cleanupMode,
            cleanupDiagnostic: cleaned.cleanupDiagnostic,
            durationMs: cleaned.durationMs,
            sourceDurationMs: cleaned.sourceDurationMs,
            leadingTrimMs: cleaned.leadingTrimMs,
            trailingTrimMs: cleaned.trailingTrimMs,
            sourceMimeType,
            sourceBytes: rawBlob.size,
            sourceChunkCount: sourceChunks.length,
            recordedAt: Date.now()
          };
          const saved = await saveVoiceLine(record);
          setClips((current) => new Map(current).set(id, saved.record));
          setStorageStatus(saved.storage);
          if (saved.storage.mode === "session") {
            onToast("Take saved for this session only · keep this page open");
          } else if (cleaned.cleanupMode === "raw-fallback") {
            console.info("[voice-lines] Original recording kept after cleanup fallback", {
              mimeType: sourceMimeType,
              sizeBytes: rawBlob.size,
              chunkCount: sourceChunks.length,
              cleanupDiagnostic: cleaned.cleanupDiagnostic
            });
            onToast(`Original take saved · ${recordingFormatLabel(sourceMimeType)} cleanup skipped`);
          } else {
            onToast("Silence trimmed · voice line saved locally");
          }
        } catch (reason) {
          setError(
            reason instanceof Error && reason.message === "NO_SPEECH_DETECTED"
              ? "No clear speech was found, so nothing was saved. Try again a little closer to the microphone."
              : reason instanceof Error && reason.message === "INVALID_RECORDING"
                ? "The recording was too short or empty. Hold Record a little longer and try again."
                : "The take could not be kept even for this session. Please try once more."
          );
        } finally {
          setProcessingId(null);
        }
      };

      // A single self-contained result is substantially more reliable than
      // concatenated timeslices for Safari's MP4/AAC MediaRecorder output.
      recordingStartedAt = performance.now();
      recorder.start();
      recordingTimeoutRef.current = window.setTimeout(stopRecording, MAX_RECORDING_MS);
    } catch (reason) {
      setRequestingId(null);
      const permissionDenied =
        reason instanceof DOMException && ["NotAllowedError", "PermissionDeniedError"].includes(reason.name);
      setError(
        permissionDenied
          ? "Microphone access was not allowed. Nothing was recorded."
          : "The microphone could not start. Try again from a secure page."
      );
    }
  };

  const stopPreview = () => {
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setPlayingId(null);
  };

  const preview = async (id: VoiceLineId) => {
    stopPreview();
    const clip = clips.get(id);
    if (!clip) return;
    const url = URL.createObjectURL(clip.blob);
    const audio = new Audio(url);
    previewUrlRef.current = url;
    previewAudioRef.current = audio;
    audio.onended = stopPreview;
    audio.onerror = stopPreview;
    setPlayingId(id);
    try {
      await audio.play();
    } catch {
      stopPreview();
      setError("The browser interrupted playback. Tap Play again.");
    }
  };

  const remove = async (id: VoiceLineId) => {
    if (playingId === id) stopPreview();
    try {
      const deletionStorage = await deleteVoiceLine(id);
      setClips((current) => {
        const next = new Map(current);
        next.delete(id);
        return next;
      });
      if (deletionStorage.mode === "session") {
        setStorageStatus(deletionStorage);
        onToast("Removed from this session · persistent storage unavailable");
      } else {
        onToast("Voice line deleted");
      }
    } catch {
      setError("That voice line could not be deleted.");
    }
  };

  return (
    <motion.div
      className="sheet-backdrop sheet-backdrop--high"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.section
        className="voice-lines-sheet"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 310 }}
      >
        <header className="voice-lines-sheet__header">
          <div>
            <span className="eyebrow">
              <AudioWaveform size={13} /> Personal soundboard
            </span>
            <h2>Your voice at Court</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            disabled={Boolean(requestingId || recordingId || processingId)}
            aria-label={requestingId || recordingId || processingId ? "Wait for recording cleanup before closing" : "Close custom voice lines"}
          >
            <X size={18} />
          </button>
        </header>

        <div className="voice-local-note">
          <LockKeyhole size={20} />
          <span>
            <b>Private to this browser</b>
            Mic starts only after Record. Quiet edges trim here with {VOICE_TRIM_LEADING_PAD_MS}ms up front and{" "}
            {VOICE_TRIM_TRAILING_PAD_MS}ms of extra tail room.
          </span>
          <strong>
            {clips.size}/{VOICE_LINE_PROMPTS.length}
          </strong>
        </div>

        {storageStatus?.mode === "session" && (
          <div className="voice-storage-notice" role="status">
            <TriangleAlert size={18} />
            <span>
              <b>Recordings are session-only</b>
              Safari is not accepting persistent storage here. New takes will still play in this game, but disappear
              after this page is refreshed or closed.
              {storageStatus.diagnostic && (
                <small>Storage code: {storageStatus.diagnostic}</small>
              )}
            </span>
          </div>
        )}

        {error && (
          <p className="voice-lines-sheet__error" role="alert">
            {error}
          </p>
        )}

        <div className="voice-lines-sheet__scroll">
          {GROUPS.map((group) => (
            <section className="voice-group" key={group.id}>
              <h3 style={{ "--voice-group-color": group.color } as React.CSSProperties}>
                {group.label}
              </h3>
              {VOICE_LINE_PROMPTS.filter((prompt) => prompt.group === group.id).map((prompt, index) => {
                const clip = clips.get(prompt.id);
                const isRequesting = requestingId === prompt.id;
                const isRecording = recordingId === prompt.id;
                const isProcessing = processingId === prompt.id;
                const isPlaying = playingId === prompt.id;
                const trimmedMs = Math.round((clip?.leadingTrimMs ?? 0) + (clip?.trailingTrimMs ?? 0));
                const storageSuffix = clip?.storageMode === "session" ? " · this session only" : "";
                const transientStatus = isRequesting
                  ? "Waiting for microphone permission"
                  : isRecording
                    ? "Recording · tap Stop when finished"
                    : isProcessing
                      ? "Cleaning audio locally"
                      : null;
                return (
                  <article className={`voice-cue ${clip ? "voice-cue--ready" : ""}`} key={prompt.id}>
                    <span className="voice-cue__number">{String(index + 1).padStart(2, "0")}</span>
                    <span className="voice-cue__copy">
                      <small>{prompt.trigger}</small>
                      <b>{prompt.title}</b>
                      <em>
                        {clip
                          ? clip.cleanupMode === "raw-fallback"
                            ? `${(clip.durationMs / 1_000).toFixed(1)} sec · original kept · ${recordingFormatLabel(clip.sourceMimeType)}${storageSuffix}`
                            : `${(clip.durationMs / 1_000).toFixed(1)} sec · ${trimmedMs > 0 ? `${trimmedMs}ms quiet trimmed` : "edges cleaned"}${storageSuffix}`
                          : `“${prompt.example}”`}
                      </em>
                    </span>
                    <span className="voice-cue__controls">
                      {clip && (
                        <>
                          <button
                            className={`voice-control voice-control--play ${isPlaying ? "is-active" : ""}`}
                            type="button"
                            onClick={() => (isPlaying ? stopPreview() : preview(prompt.id))}
                            disabled={Boolean(requestingId || recordingId || processingId)}
                            aria-label={`${isPlaying ? "Stop" : "Play"} ${prompt.title}`}
                          >
                            {isPlaying ? <Square size={15} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
                          </button>
                          <button
                            className="voice-control voice-control--delete"
                            type="button"
                            onClick={() => remove(prompt.id)}
                            disabled={Boolean(requestingId || recordingId || processingId)}
                            aria-label={`Delete ${prompt.title}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                      <button
                        className={`voice-record ${isRequesting ? "is-requesting" : ""} ${isRecording ? "is-recording" : ""} ${isProcessing ? "is-processing" : ""}`}
                        type="button"
                        onClick={() => (isRecording ? stopRecording() : startRecording(prompt.id))}
                        disabled={Boolean(processingId || requestingId || (recordingId && !isRecording))}
                        aria-label={`${isProcessing ? "Trimming recording" : isRequesting ? "Waiting for microphone permission" : isRecording ? "Stop recording" : clip ? "Replace recording" : "Record"} ${prompt.title}`}
                      >
                        {isProcessing ? (
                          <ScissorsLineDashed size={17} />
                        ) : isRecording ? (
                          <Square size={16} fill="currentColor" />
                        ) : (
                          <Mic2 size={17} />
                        )}
                        <span>{isProcessing || isRequesting ? "Wait" : isRecording ? "Stop" : clip ? "Redo" : "Record"}</span>
                      </button>
                    </span>
                    {transientStatus && (
                      <span
                        className={`voice-cue__status ${isRecording ? "is-recording" : isProcessing ? "is-processing" : "is-requesting"}`}
                        role="status"
                        aria-live="polite"
                      >
                        {isProcessing ? <ScissorsLineDashed size={13} /> : <Mic2 size={13} />}
                        {transientStatus}
                      </span>
                    )}
                  </article>
                );
              })}
            </section>
          ))}
          <p className="voice-lines-sheet__footnote">
            Lines play only for your own action, block, or challenge taps on this device. Room sharing is intentionally outside this prototype.
          </p>
        </div>
      </motion.section>
    </motion.div>
  );
}
